import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Shield, Trash2, Search } from "lucide-react";

interface Profile {
  id: string;
  username: string;
  full_name: string | null;
  email?: string;
}

interface UserRole {
  id: string;
  user_id: string;
  role: string;
  granted_at: string;
  user_profile?: Profile;
}

const RoleManagement = () => {
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchUserRoles();
  }, []);

  const fetchUserRoles = async () => {
    try {
      const { data: rolesData, error } = await supabase
        .from("user_roles")
        .select("*")
        .order("granted_at", { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      const userIds = rolesData?.map(r => r.user_id) || [];
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username, full_name, avatar_url, bio, created_at, updated_at, user_type, verification_status, verified_at, city, country, business_name, subscription_status, latitude, longitude, is_live, last_active_at, profile_views, theme_preference, upgraded_at, portal_id, account_status")
          .in("id", userIds);

        const rolesWithProfiles = rolesData?.map(role => ({
          ...role,
          user_profile: profilesData?.find(p => p.id === role.user_id)
        })) || [];

        setUserRoles(rolesWithProfiles);
      } else {
        setUserRoles([]);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading roles",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const searchUser = async () => {
    if (!searchEmail) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter a user email to search",
      });
      return;
    }

    try {
      // Search for profile by email in profiles table
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, username, full_name, avatar_url, bio, created_at, updated_at, user_type, verification_status, verified_at, city, country, business_name, subscription_status, latitude, longitude, is_live, last_active_at, profile_views, theme_preference, upgraded_at, portal_id, account_status")
        .ilike("username", `%${searchEmail}%`)
        .limit(1)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile) {
        toast({
          variant: "destructive",
          title: "User not found",
          description: "No user found with that email or username",
        });
        return;
      }

      setSelectedUser({ id: profile.id, profile });
      toast({
        title: "User found",
        description: `Found ${profile.full_name || profile.username}`,
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Search failed",
        description: error.message,
      });
    }
  };

  const grantRole = async () => {
    if (!selectedUser || !selectedRole) {
      toast({
        variant: "destructive",
        title: "Missing information",
        description: "Please select a user and role",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("user_roles")
        .insert([{
          user_id: selectedUser.id,
          role: selectedRole as any,
        }]);

      if (error) throw error;

      toast({
        title: "Role granted",
        description: `Successfully granted ${selectedRole} access`,
      });

      fetchUserRoles();
      setSelectedUser(null);
      setSearchEmail("");
      setSelectedRole("");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error granting role",
        description: error.message,
      });
    }
  };

  const revokeRole = async (roleId: string) => {
    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", roleId);

      if (error) throw error;

      toast({
        title: "Role revoked",
        description: "Access has been removed",
      });

      fetchUserRoles();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error revoking role",
        description: error.message,
      });
    }
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: "bg-red-500/20 text-red-300 border-red-500/40",
      platform_control: "bg-purple-500/20 text-purple-300 border-purple-500/40",
      creator_management: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      venue_oversight: "bg-green-500/20 text-green-300 border-green-500/40",
    };

    const labels: Record<string, string> = {
      admin: "Admin",
      platform_control: "Platform Control",
      creator_management: "Creator Management",
      venue_oversight: "Venue Oversight",
    };

    return (
      <Badge className={colors[role] || "bg-gray-500/20 text-gray-300 border-gray-500/40"}>
        {labels[role] || role}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-white">Loading roles...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Role Management</h2>
        <p className="text-gray-400">Grant access to enterprise management dashboards</p>
      </div>

      {/* Grant New Role */}
      <Card className="bg-black/40 border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Grant Dashboard Access
          </CardTitle>
          <CardDescription className="text-gray-400">
            Search for a user and assign them management access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="email" className="text-white">Username or Email</Label>
              <Input
                id="email"
                type="text"
                placeholder="username or email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="bg-white/5 border-white/20 text-white"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={searchUser} className="bg-white text-black hover:bg-gray-200">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>

          {selectedUser && (
            <div className="bg-white/5 p-4 rounded-lg space-y-4">
              <div>
                <p className="text-white font-semibold">
                  {selectedUser.profile?.full_name || selectedUser.profile?.username}
                </p>
                <p className="text-gray-400 text-sm">@{selectedUser.profile?.username}</p>
              </div>

              <div>
                <Label htmlFor="role" className="text-white">Select Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="creator_management">Creator Management</SelectItem>
                    <SelectItem value="venue_oversight">Venue Oversight</SelectItem>
                    <SelectItem value="platform_control">Platform Control</SelectItem>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={grantRole} className="w-full bg-green-500 hover:bg-green-600 text-white">
                <Shield className="w-4 h-4 mr-2" />
                Grant Access
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Roles */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-white">Current Access Grants</h3>
        <div className="grid gap-4">
          {userRoles.map((userRole) => (
            <Card key={userRole.id} className="bg-black/40 border-white/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-white font-semibold">
                        {userRole.user_profile?.full_name || userRole.user_profile?.username || 'Unknown User'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        @{userRole.user_profile?.username || 'unknown'}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Granted: {new Date(userRole.granted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      {getRoleBadge(userRole.role)}
                    </div>
                  </div>
                  <Button
                    onClick={() => revokeRole(userRole.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-400 border-red-500/40 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Revoke
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {userRoles.length === 0 && (
            <Card className="bg-black/40 border-white/20">
              <CardContent className="py-12 text-center">
                <Shield className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No roles granted yet</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
